import { useMemo } from "react";
import { Link } from "react-router";
import { addDays, differenceInCalendarDays, format } from "date-fns";
import { nl } from "date-fns/locale";
import { Body, Caption, Card, CardTitle, cn } from "@/components/ui";
import { useAssets, useReservations } from "@/domains/assets";
import { assetTypeLabel } from "@/domains/assets";
import type { AssetReservation } from "@/domains/assets";
import { useCases } from "@/domains/cases";

type GanttViewProps = {
  rangeStart: string;
  rangeDays?: number;
};

export function GanttView({
  rangeStart,
  rangeDays = 21,
}: GanttViewProps): React.JSX.Element {
  const { data: assets } = useAssets();
  const { data: reservations } = useReservations();
  const { data: cases } = useCases();

  const caseAddressById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cases ?? []) map.set(c.id, c.address);
    return map;
  }, [cases]);

  const caseIdsThatExist = useMemo(() => {
    return new Set((cases ?? []).map((c) => c.id));
  }, [cases]);

  const days = useMemo(() => {
    const start = new Date(rangeStart);
    return Array.from({ length: rangeDays }, (_, i) => addDays(start, i));
  }, [rangeStart, rangeDays]);

  const reservationsByAsset = useMemo(() => {
    const map = new Map<string, Array<AssetReservation>>();
    for (const r of reservations ?? []) {
      const arr = map.get(r.assetId) ?? [];
      arr.push(r);
      map.set(r.assetId, arr);
    }
    return map;
  }, [reservations]);

  const rangeStartDate = new Date(rangeStart);
  const rangeEndDate = addDays(rangeStartDate, rangeDays - 1);

  return (
    <Card padding="lg" className="space-y-3 overflow-x-auto">
      <div className="flex items-center justify-between">
        <CardTitle>Asset-bezetting</CardTitle>
        <Caption tone="muted">
          {format(rangeStartDate, "d MMM", { locale: nl })} →{" "}
          {format(rangeEndDate, "d MMM yyyy", { locale: nl })}
        </Caption>
      </div>

      <div className="min-w-[900px]">
        <div
          className="grid items-end gap-x-1 border-b border-border pb-1"
          style={{ gridTemplateColumns: `12rem repeat(${rangeDays}, 1fr)` }}
        >
          <div />
          {days.map((d) => (
            <div key={d.toISOString()} className="text-center">
              <Caption tone="muted" className="block">
                {format(d, "EEE", { locale: nl })}
              </Caption>
              <span className="text-xs font-semibold">
                {format(d, "d/M", { locale: nl })}
              </span>
            </div>
          ))}
        </div>

        <ul className="space-y-1 pt-2">
          {(assets ?? []).map((asset) => {
            const assetReservations = reservationsByAsset.get(asset.id) ?? [];
            return (
              <li
                key={asset.id}
                className="grid items-center gap-x-1 rounded-md py-1 hover:bg-muted/40"
                style={{ gridTemplateColumns: `12rem repeat(${rangeDays}, 1fr)` }}
              >
                <Link
                  to={`/assets/${asset.id}`}
                  className="truncate pr-2 text-sm hover:underline"
                >
                  <Body weight="semibold" className="truncate">
                    {asset.number}
                  </Body>
                  <Caption tone="muted">{assetTypeLabel(asset.type)}</Caption>
                </Link>
                <div
                  className="col-span-full col-start-2 grid h-7 items-center"
                  style={{ gridTemplateColumns: `repeat(${rangeDays}, 1fr)` }}
                >
                  {Array.from({ length: rangeDays }).map((_, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        "h-full border-r border-border/40",
                        idx === rangeDays - 1 && "border-r-0",
                      )}
                    />
                  ))}
                </div>
                {assetReservations.map((r) => {
                  const rStart = new Date(r.startDate);
                  const rEnd = new Date(r.endDate);
                  if (rEnd < rangeStartDate || rStart > rangeEndDate) return null;
                  const clippedStart =
                    rStart < rangeStartDate ? rangeStartDate : rStart;
                  const clippedEnd = rEnd > rangeEndDate ? rangeEndDate : rEnd;
                  const startOffset = differenceInCalendarDays(clippedStart, rangeStartDate);
                  const span = differenceInCalendarDays(clippedEnd, clippedStart) + 1;
                  const address = caseAddressById.get(r.caseId);
                  const caseLabel = `#${r.caseId.replace("case-", "")}`;
                  const caseExists = caseIdsThatExist.has(r.caseId);
                  const tooltip = `Dossier ${caseLabel} — ${r.startDate} → ${r.endDate}${address ? ` — ${address}` : ""}${caseExists ? "" : " (historisch)"}`;
                  const sharedClass =
                    "col-start-[--col-start] -mt-7 flex h-6 items-center gap-1.5 overflow-hidden rounded px-2 text-xs font-semibold";
                  const style = {
                    gridColumnStart: startOffset + 2,
                    gridColumnEnd: `span ${span}`,
                  };
                  const content = (
                    <>
                      <span className="shrink-0">{caseLabel}</span>
                      {address ? (
                        <span className="truncate font-normal text-on-primary/80">
                          {address}
                        </span>
                      ) : null}
                    </>
                  );
                  if (caseExists) {
                    return (
                      <Link
                        key={r.id}
                        to={`/cases/${r.caseId}`}
                        className={cn(
                          sharedClass,
                          "bg-primary-600 text-on-primary hover:bg-primary-700",
                        )}
                        style={style}
                        title={tooltip}
                      >
                        {content}
                      </Link>
                    );
                  }
                  return (
                    <div
                      key={r.id}
                      className={cn(
                        sharedClass,
                        "cursor-default bg-neutral-300 text-neutral-700",
                      )}
                      style={style}
                      title={tooltip}
                    >
                      {content}
                    </div>
                  );
                })}
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
}
